import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemIcon,
    Checkbox,
    ListItemText,
    Snackbar,
    IconButton,
    ListItemButton,
    Collapse,
    ListSubheader
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import RefreshIcon from '@mui/icons-material/Refresh';

// Import Service functions and types
import { 
    trainingManagementService, 
    Dataset, 
    SourceGroup,
    CameraInfo
} from '../../../service/trainingManagementService';

// Helper to get status chip for Datasets
const getDatasetStatusChip = (status?: string) => {
    switch (status) {
        case 'READY':
            return <Chip label="Bereit" color="success" size="small" icon={<CheckCircleIcon />} />;
        case 'CREATING':
            return <Chip label="Wird erstellt..." color="info" size="small" icon={<PendingIcon />} />;
        case 'FAILED':
            return <Chip label="Fehlgeschlagen" color="error" size="small" icon={<ErrorIcon />} />;
        default:
            return <Chip label={status || 'Unbekannt'} size="small" />;
    }
};

const DatasetManagerTab: React.FC = () => {
    const [datasets, setDatasets] = useState<Dataset[]>([]);
    const [sourceGroups, setSourceGroups] = useState<SourceGroup[]>([]);
    const [selectedSources, setSelectedSources] = useState<string[]>([]);
    const [newDatasetName, setNewDatasetName] = useState<string>('');
    const [loadingDatasets, setLoadingDatasets] = useState(true);
    const [loadingSources, setLoadingSources] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [createError, setCreateError] = useState<string | null>(null);
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    // --- Data Fetching --- 
    const loadDatasets = useCallback(async () => {
        setLoadingDatasets(true);
        setError(null);
        try {
            const fetchedDatasets = await trainingManagementService.getDatasets();
            setDatasets(fetchedDatasets);
        } catch (err: any) {
            console.error("Error loading datasets:", err);
            setError(`Fehler beim Laden der Datasets: ${err.message || 'Unbekannter Fehler'}`);
        } finally {
            setLoadingDatasets(false);
        }
    }, []);

    const loadSourceGroups = useCallback(async () => {
        setLoadingSources(true);
        setError(null);
        setSourceGroups([]);
        try {
            const fetchedSourceGroups = await trainingManagementService.getAccountCameraInfo();
            
            // --- DEBUG: Log the fetched data structure ---
            console.log("Fetched Source Groups:", JSON.stringify(fetchedSourceGroups, null, 2));
            // --- END DEBUG ---
            
            setSourceGroups(fetchedSourceGroups);

        } catch (err: any) {
            console.error("Error loading source groups:", err);
            setError(`Fehler beim Laden der Quellen (Accounts): ${err.message || 'Unbekannter Fehler'}.`);
        } finally {
            setLoadingSources(false);
        }
    }, []);

    useEffect(() => {
        loadDatasets();
    }, [loadDatasets]);

    // --- Dialog and Creation Logic --- 
    const handleOpenCreateDialog = () => {
        setNewDatasetName('');
        setSelectedSources([]);
        setCreateError(null);
        setSourceGroups([]);
        setOpenCreateDialog(true);
        loadSourceGroups();
    };

    const handleCloseCreateDialog = () => {
        setOpenCreateDialog(false);
    };

    const handleSourceToggle = (accountName: string) => () => {
        const currentIndex = selectedSources.indexOf(accountName);
        const newSelectedSources = [...selectedSources];

        if (currentIndex === -1) {
            newSelectedSources.push(accountName);
        } else {
            newSelectedSources.splice(currentIndex, 1);
        }
        setSelectedSources(newSelectedSources);
    };

    const handleCreateDataset = async () => {
        if (!newDatasetName.trim() || selectedSources.length === 0) {
            setCreateError('Bitte geben Sie einen Namen ein und wählen Sie mindestens einen Account (Waschstraße) aus.');
            return;
        }
        if (!/^[a-zA-Z0-9_-]+$/.test(newDatasetName.trim())) {
             setCreateError('Dataset-Name darf nur Buchstaben, Zahlen, Unterstriche und Bindestriche enthalten.');
             return;
        }

        setIsCreating(true);
        setCreateError(null);

        try {
            const result = await trainingManagementService.createDataset(
                newDatasetName.trim(),
                selectedSources
            );
            setSnackbarMessage(result.message || 'Dataset-Erstellung gestartet.');
            setSnackbarOpen(true);
            handleCloseCreateDialog();
            setTimeout(loadDatasets, 1000);
        } catch (err: any) {
            console.error("Error creating dataset:", err);
            setCreateError(`Fehler beim Erstellen des Datasets: ${err.message || 'Unbekannter Fehler'}`);
        } finally {
            setIsCreating(false);
        }
    };

     const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
        setSnackbarMessage('');
    };

    // --- Render --- 
    if (loadingDatasets) {
        return (
             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Lade Datasets...</Typography>
            </Box>
        );
    }

    return (
        <Box>
             <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                 <Typography variant="h6">Datasets</Typography>
                 <Box>
                     <IconButton onClick={loadDatasets} disabled={loadingDatasets} title="Liste aktualisieren" sx={{ mr: 1}}>
                          <RefreshIcon />
                     </IconButton>
                    <Button 
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateDialog}
                    >
                        Neues Dataset erstellen
                    </Button>
                 </Box>
            </Box>

            {error && !openCreateDialog && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Dataset List Table */} 
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="datasets table" size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Beschreibung</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Erstellt am</TableCell>
                            <TableCell>Aktualisiert am</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingDatasets ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell>
                            </TableRow>
                        ) : datasets.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">Keine Datensätze gefunden.</TableCell>
                            </TableRow>
                        ) : (
                            datasets.map((dataset) => (
                                <TableRow key={dataset._id}>
                                    <TableCell>{dataset.name}</TableCell>
                                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {dataset.description || '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={dataset.status || 'Unbekannt'} 
                                            color={dataset.status === 'READY' ? 'success' : (dataset.status === 'CREATING' ? 'info' : 'default')} 
                                            size="small" 
                                        />
                                    </TableCell>
                                    <TableCell>{dataset.created_at ? new Date(dataset.created_at).toLocaleString() : 'N/A'}</TableCell>
                                    <TableCell>{dataset.updated_at ? new Date(dataset.updated_at).toLocaleString() : 'N/A'}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create Dataset Dialog */} 
            <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Neues Dataset erstellen</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Dataset Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newDatasetName}
                        onChange={(e) => setNewDatasetName(e.target.value)}
                        error={!!createError && !createError.includes("Account")}
                        helperText={createError && !createError.includes("Account") ? createError : "Nur Buchstaben, Zahlen, _, - erlaubt"}
                        sx={{ mb: 2 }}
                    />

                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Accounts (Waschstraßen) auswählen:</Typography>
                    {loadingSources && 
                        <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
                            <CircularProgress size={20} sx={{ mr: 1 }}/> 
                            <Typography variant="body2">Lade Accounts...</Typography>
                         </Box>
                    }
                    {error && openCreateDialog && error.includes("Quellen") &&
                         <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
                    }
                    {createError && createError.includes("Account") && 
                         <Alert severity="error" sx={{ mt: 1 }}>{createError}</Alert>
                    }
                    {!loadingSources && !error && sourceGroups.length === 0 && (
                        <Typography sx={{ my: 2, color: 'text.secondary' }}>Keine Accounts gefunden.</Typography>
                    )}
                    
                    {!loadingSources && sourceGroups.length > 0 && (
                         <Paper sx={{ maxHeight: 300, overflow: 'auto', mt: 1, border: 1, borderColor: 'divider' }}>
                             <List dense component="div" role="list">
                                 {sourceGroups.map((group) => {
                                     const labelId = `checkbox-list-label-${group.accountName}`;
                                     const isSelected = selectedSources.includes(group.accountName);

                                     return (
                                         <ListItem
                                             key={group.accountId}
                                             role="listitem"
                                             component={ListItemButton}
                                             onClick={handleSourceToggle(group.accountName)}
                                             dense
                                         >
                                             <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                                                 <Checkbox
                                                     edge="start"
                                                     checked={isSelected}
                                                     tabIndex={-1}
                                                     disableRipple
                                                     inputProps={{ 'aria-labelledby': labelId }}
                                                 />
                                             </ListItemIcon>
                                             <ListItemText id={labelId} primary={group.accountName} />
                                         </ListItem>
                                     );
                                 })}
                             </List>
                         </Paper>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreateDialog} disabled={isCreating}>Abbrechen</Button>
                    <Button 
                        onClick={handleCreateDataset} 
                        variant="contained" 
                        disabled={isCreating || loadingSources || selectedSources.length === 0 || !newDatasetName.trim()}
                    >
                        {isCreating ? <CircularProgress size={24} /> : 'Erstellen'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for feedback */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={snackbarMessage}
            />
        </Box>
    );
};

export default DatasetManagerTab; 